import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

/**
 * Log page view to GA4, Firebase Analytics & Meta Pixel
 */
export function trackPageView(path: string, title?: string) {
  try {
    const pageTitle = title || document.title;
    const pageLocation = window.location.href;

    // GA4 via gtag
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_location: pageLocation,
        page_path: path,
      });
    }

    // Meta Pixel
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }

    // Firebase Analytics SDK
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: pageTitle,
        page_location: pageLocation,
        page_path: path,
      });
    }
  } catch (err) {
    // Fail silently in development/test
  }
}

/**
 * Log custom business event
 */
export function trackEvent(eventName: string, params: Record<string, any> = {}) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', eventName, params);
    }
    if (analytics) {
      logEvent(analytics, eventName, params);
    }
  } catch (err) {
    // Fail silently
  }
}

/**
 * Pre-configured Conversion Funnel Events
 */
export const ConversionFunnel = {
  // Funnel 1: Onboarding / Registration
  viewPricing: (planName?: string) => {
    trackEvent('view_pricing', { plan_selected: planName || 'all' });
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', {
        content_name: 'Pricing Plans',
        content_category: 'Subscription',
      });
    }
  },
  startSignUp: (source: string = 'header') => {
    trackEvent('begin_registration', { signup_source: source });
  },
  completeSignUp: (userId: string, email: string, plan: string = 'free_trial') => {
    trackEvent('sign_up', {
      method: 'email_password',
      user_id: userId,
      plan_type: plan,
    });
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'CompleteRegistration', {
        content_name: plan,
        status: 'success',
      });
    }
  },

  // Funnel 2: Core Product Activation (Sending Documents)
  startDocumentCreation: (type: 'kviksign' | 'kviksend' | 'kvikid' | 'kvikform') => {
    trackEvent('start_document_creation', { module: type });
  },
  uploadDocumentFile: (fileType: string, fileSizeKb: number) => {
    trackEvent('document_file_uploaded', { file_type: fileType, size_kb: fileSizeKb });
  },
  sendDocumentComplete: (documentId: string, recipientsCount: number, authMethods: string[]) => {
    trackEvent('send_document_success', {
      document_id: documentId,
      recipients_count: recipientsCount,
      auth_methods: authMethods.join(','),
      value: 1, // Conversion value
    });
    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'SendDocument', {
        document_id: documentId,
        recipients_count: recipientsCount,
      });
    }
  },

  // Funnel 3: Subscription & Billing
  beginCheckout: (planId: string, priceDkk: number) => {
    trackEvent('begin_checkout', {
      items: [{ item_name: planId, price: priceDkk, currency: 'DKK' }],
      value: priceDkk,
      currency: 'DKK',
    });
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', {
        content_ids: [planId],
        value: priceDkk,
        currency: 'DKK',
      });
    }
  },
  purchaseSuccess: (transactionId: string, planId: string, amount: number) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      value: amount,
      currency: 'DKK',
      items: [{ item_name: planId, price: amount }],
    });
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Purchase', {
        value: amount,
        currency: 'DKK',
        content_ids: [planId],
        content_type: 'product',
      });
    }
  },

  // Funnel 4: Leads & Contact Inquiries (Standard Meta Pixel: Lead & Contact)
  contactLead: (formType: string = 'contact_us', details: Record<string, any> = {}) => {
    trackEvent('generate_lead', { form_type: formType, ...details });
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', {
        content_name: formType,
        ...details,
      });
      window.fbq('track', 'Contact');
    }
  },
};

/**
 * Core Web Vitals Monitor (LCP, FID/INP, CLS, FCP, TTFB)
 * Tracks actual user experience metrics from day 1
 */
export function initWebVitalsTracker() {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  const reportMetric = (name: string, value: number, rating: 'good' | 'needs-improvement' | 'poor') => {
    trackEvent('web_vitals', {
      metric_name: name,
      metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
      metric_rating: rating,
      non_interaction: true,
    });
  };

  try {
    // 1. Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        const value = lastEntry.startTime;
        const rating = value < 2500 ? 'good' : value < 4000 ? 'needs-improvement' : 'poor';
        reportMetric('LCP', value, rating);
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

    // 2. First Input Delay / Interaction (FID)
    const fidObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as any[]) {
        const value = entry.processingStart - entry.startTime;
        const rating = value < 100 ? 'good' : value < 300 ? 'needs-improvement' : 'poor';
        reportMetric('FID', value, rating);
      }
    });
    fidObserver.observe({ type: 'first-input', buffered: true });

    // 3. Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report CLS on page unload / visibilitychange
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        const rating = clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs-improvement' : 'poor';
        reportMetric('CLS', clsValue, rating);
      }
    }, { once: true });

    // 4. Time to First Byte (TTFB) & First Contentful Paint (FCP)
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries.length > 0) {
      const nav = navEntries[0] as PerformanceNavigationTiming;
      const ttfb = nav.responseStart - nav.requestStart;
      if (ttfb > 0) {
        const rating = ttfb < 800 ? 'good' : ttfb < 1800 ? 'needs-improvement' : 'poor';
        reportMetric('TTFB', ttfb, rating);
      }
    }
  } catch (err) {
    // Ignore observer restrictions
  }
}

/**
 * Client-Side Error Tracker
 */
export function trackAppError(error: Error | string, context: string = 'general') {
  const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
  const stack = typeof error === 'object' && error?.stack ? error.stack.slice(0, 300) : '';

  trackEvent('exception', {
    description: `[${context}] ${errorMessage}`,
    fatal: false,
    stack_trace: stack,
  });
}
